<template>
  <div class="audioSettingsModalBackdrop">
    <div :class="['audioSettingsModal', { noPermissions: !hasMicrophonePermission }]">
      <div v-if="hasMicrophonePermission" class="header">
        <h2>{{ steps[currentStep].title }}</h2>
        <MButton
          v-if="!requiresAudioDevicePromptForced"
          variant="danger"
          :icon="faTimes"
          @click="onCloseDevicePrompt"
          iconPosition="center"
          size="sm"
        />
      </div>

      <div class="body" v-if="hasMicrophonePermission">
        <div v-if="currentStep === 0" class="requirements">
          <RequirementsNotice />
        </div>

        <!-- DEVICE SELECTION -->
        <div v-else class="deviceSelection">
          <p>Selected device:</p>
          <DeviceSelect
            ref="deviceSelect"
            v-model="selectedDevice"
            :options="deviceOptions"
            :placeholder="dropdownDisabled ? '' : 'Please select a device'"
            :disabled="dropdownDisabled"
            @focus="onDropdownFocus"
            @blur="onDropdownBlur"
          />

          <p>{{ steps[currentStep].volumeLabel }}</p>

          <!-- AUDIO ANALYSER -->
          <AudioSettingsAnalyser
            :key="currentStep"
            :device-kind="steps[currentStep].deviceKind"
            :dropdown-focused="dropdownFocused"
            @sound-picked="onSoundPicked"
            @playback-finished="onPlaybackFinished"
            ref="audioAnalyser"
          />

          <!-- NO AUDIO INPUT ERROR -->
          <div
            v-if="showNoAudioInputError && currentDeviceType === 'audioinput'"
            class="info error"
          >
            <FontAwesomeIcon class="warningTriangle" :icon="faExclamationTriangle" size="xl" />
            <p>
              No sound detected from selected microphone, try choosing another microphone from the
              list below. Talk into your microphone to test it's working.
            </p>
          </div>

          <!-- INFO -->
          <div class="info">
            <FontAwesomeIcon class="dangerTriangle" :icon="faInfoCircle" size="xl" />

            <!-- INFO FOR AUDIO OUTPUT -->
            <div v-if="currentDeviceType === 'audiooutput'">
              If you can't hear the music try to adjust the volume
              <span v-if="outputSelectionSupported">
                ,
                <span class="callToAction" @click="selectNextDevice">try another device</span></span
              >
              or visit our
              <a :href="headPhonesSetupLink" target="_blank" class="callToAction">help guide</a>.
            </div>

            <!-- INFO FOR AUDIO INPUT -->
            <div v-if="currentDeviceType === 'audioinput'">
              If you can't hear the reply try ensure your microphone is connected and working
              <span v-if="devicesList.length > 1">
                ,
                <span class="callToAction" @click="selectNextDevice">try another device</span></span
              >
              or visit our
              <a :href="microphoneSetupLink" target="_blank" class="callToAction">help guide</a>.

              <div>If the device is not listed, try refreshing the page.</div>
            </div>
          </div>
        </div>
      </div>
      <div v-if="hasMicrophonePermission" class="footer">
        <div class="left">
          <MButton
            v-if="currentStep > 0"
            :title="'Go back'"
            variant="secondary"
            :icon="faArrowLeft"
            @click="onBackClick"
            iconPosition="left"
          />
        </div>

        <div class="right">
          <MButton v-if="currentStep === 0" title="Start Tech Test" @click="onConfirm" />
          <MButton
            v-else-if="currentDeviceType === 'audiooutput' && !outputPlaybackActive"
            title="Play the music!"
            @click="onPlayClick"
          />
          <MButton
            v-else
            :disabled="confirmDisabled"
            :title="steps[currentStep].buttonText"
            @click="onConfirm"
          />
        </div>
      </div>
    </div>
  </div>
  <ErrorModal
    :show="!hasMicrophonePermission"
    :error-msg="getMicPermissionMessage"
    :title="'Microphone Access Required'"
    persistent
  />
</template>

<script lang="ts" setup>
import { ref, computed, watch, onMounted, nextTick } from 'vue'
import type { Ref, ComputedRef } from 'vue'
import type { DeviceInfo, ManagedTimeout, ErrorMessage } from '@/customTypes'
import { storeToRefs } from 'pinia'

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import {
  faExclamationTriangle,
  faInfoCircle,
  faArrowLeft,
  faTimes
} from '@fortawesome/free-solid-svg-icons'

// Components
import RequirementsNotice from '@/components/RequirementsNotice.vue'
import AudioSettingsAnalyser from '@/components/AudioSettingsAnalyser.vue'
import DeviceSelect from '@/components/DeviceSelect.vue'
import MButton from '@/components/MButton.vue'
import ErrorModal from '@/components/ErrorModal.vue'

// Helpers
import { useTimeout } from '@/utils/helpers'

// Stores
import { useMediaDevicesStore } from '@/stores/MediaDevicesStore'
import { useDeviceStore } from '@/stores/DeviceStore'
import { useSoundEffectsStore } from '@/stores/SoundEffectsStore'
import { useAudioAnalyserStore } from '@/stores/AudioAnalyserStore'
import { useActiveScreenerStore } from '@/stores/activeScreenerStore'

import Logger from '@/services/Logger.js'

type DeviceKind = 'audioinput' | 'audiooutput'

type Step = {
  deviceKind: DeviceKind
  title: string
  volumeLabel?: string
  buttonText?: string
}

const logger: any = new Logger('AudioSettingsModal', true)

const emit = defineEmits<{
  (e: 'tech-test-finished'): void
}>()

const mediaDevicesStore = useMediaDevicesStore()
const activeScreenerStore = useActiveScreenerStore()
const { audioDevices } = storeToRefs(mediaDevicesStore)
const {
  isIpad,
  isSafari,
  browserType,
  browserTypes,
  outputSelectionSupported,
  requiresAudioDevicePromptForced,
  changeInputDevice,
  storeAudioDeviceId,
  logTechTestSummary
} = mediaDevicesStore

const { deviceIs } = useDeviceStore()
const { setManagedTimeout, clearManagedTimeout } = useTimeout()

const deviceSelect = ref<InstanceType<typeof DeviceSelect> | null>(null)
const audioAnalyser: Ref<any> = ref(null)
const currentStep: Ref<number> = ref(0)
const selectedDevice: Ref<string> = ref('')
const dropdownFocused: Ref<boolean> = ref(false)
let suppressSelectedDeviceWatch = false
const lastSelectedOutputDeviceId: Ref<string> = ref('')
const lastSelectedInputDeviceId: Ref<string> = ref('')
const shouldResumeOutputAfterBlur: Ref<boolean> = ref(false)

const onDropdownFocus = () => {
  dropdownFocused.value = true
  if (currentDeviceType.value === 'audiooutput') {
    shouldResumeOutputAfterBlur.value = outputPlaybackActive.value
    if (outputPlaybackActive.value) {
      stopTracks()
    }
  }
}

const onDropdownBlur = () => {
  dropdownFocused.value = false
  if (currentDeviceType.value === 'audiooutput') {
    const resumePlayback = shouldResumeOutputAfterBlur.value
    shouldResumeOutputAfterBlur.value = false
    if (resumePlayback) {
      nextTick(() => {
        onPlayClick()
      })
    }
  }
}

const setSelectedDeviceProgrammatically = async (value: string) => {
  suppressSelectedDeviceWatch = true
  try {
    selectedDevice.value = value
    await Promise.resolve()
  } finally {
    suppressSelectedDeviceWatch = false
  }
}

const { selectedAudioInputDeviceId, selectedAudioOutputDeviceId } = storeToRefs(mediaDevicesStore)

const soundEffectsStore = useSoundEffectsStore()
const { updateOutputDevice, stopTracks } = soundEffectsStore
const audioAnalyserStore = useAudioAnalyserStore()
const { outputPlaybackActive } = storeToRefs(audioAnalyserStore)

lastSelectedInputDeviceId.value = selectedAudioInputDeviceId.value || ''
lastSelectedOutputDeviceId.value = selectedAudioOutputDeviceId.value || ''

// Audio input checks
const NO_INPUT_TIMEOUT = 5000
const soundPicked: Ref<boolean> = ref(false)
const playbackPlayed: Ref<boolean> = ref(false)
const buttonDisabled: Ref<boolean> = ref(false) // used to programmatically disable the confirm button
const noAudioInputTimeoutId: Ref<ManagedTimeout | null> = ref(null)
const showNoAudioInputError: Ref<boolean> = ref(false)

const deviceAccessHelpLink: string =
  isIpad && isSafari
    ? `${_HELP_ARTICLES_URL_}/device-access-help#ipad-media-device-access`
    : `${_HELP_ARTICLES_URL_}/device-access-help#chrome-media-device-access`
const headPhonesSetupLink: string = `${_HELP_ARTICLES_URL_}/headphones-setup`
const microphoneSetupLink: string = deviceIs('IPAD')
  ? `${_HELP_ARTICLES_URL_}/microphone-setup#ipad-mic`
  : `${_HELP_ARTICLES_URL_}/microphone-setup`

const steps: Record<number, Step> = {
  0: {
    title: 'Screener Setup Requirements',
    deviceKind: 'audioinput'
  },
  1: {
    deviceKind: 'audiooutput',
    title: 'Do you hear the music?',
    volumeLabel: 'Output level:',
    buttonText: 'Yes, I hear it!'
  },
  2: {
    deviceKind: 'audioinput',
    title: 'Speak and pause, can you hear the replay?',
    volumeLabel: 'Input level:',
    buttonText: 'Yes, I hear the replay!'
  }
}

const dropdownDisabled: ComputedRef<boolean> = computed(() => {
  return (
    (currentDeviceType.value === 'audiooutput' && !outputSelectionSupported) ||
    audioDevices.value.length <= 1
  )
})

const hasMicrophonePermission: ComputedRef<boolean> = computed(() => {
  return mediaDevicesStore.microphonePermission
})

const devicesList: ComputedRef<DeviceInfo[]> = computed(() => {
  logger.info('Computing new devices list')

  const isDefaultOption = (device: DeviceInfo) => !device.deviceId || device.deviceId === 'default'

  const storedDeviceId =
    currentDeviceType.value === 'audioinput'
      ? selectedAudioInputDeviceId.value
      : selectedAudioOutputDeviceId.value

  let list = audioDevices.value.reduce((deviceList: DeviceInfo[], device: DeviceInfo) => {
    if (device.kind !== currentDeviceType.value) return deviceList

    if (storedDeviceId && device.deviceId === storedDeviceId) {
      deviceList.unshift(device)
    } else {
      deviceList.push(device)
    }

    return deviceList
  }, [])

  if (dropdownDisabled.value) {
    const fallbackDevice =
      storedDeviceId &&
      list.find((device) => device.deviceId === storedDeviceId && !isDefaultOption(device))
    const firstAlternative = list.find((device) => !isDefaultOption(device))

    const defaultOnly = list.length === 0 || list.every(isDefaultOption)
    const finalDevice = fallbackDevice || firstAlternative

    if (defaultOnly) {
      list = [
        {
          deviceId: 'default',
          label: '---- Same as system ----'
        }
      ]
    } else if (finalDevice) {
      list = [finalDevice]
    }
  } else if (!list.length) {
    list = [
      {
        deviceId: '',
        label: 'No audio devices found'
      }
    ]
  }

  return list
})

const deviceOptions = computed(() =>
  devicesList.value.map((device) => ({
    value: String(device.deviceId ?? ''),
    label: device.label || ''
  }))
)

const currentDeviceType: ComputedRef<DeviceKind | ''> = computed(() => {
  return steps[currentStep.value].deviceKind || ''
})

const confirmDisabled: ComputedRef<boolean> = computed(() => {
  return (
    !soundPicked.value ||
    (currentDeviceType.value === 'audioinput' && !playbackPlayed.value) ||
    buttonDisabled.value
  )
})

const getMicPermissionMessage = computed(() => {
  return {
    type: 'danger',
    message:
      browserType === browserTypes.CHROME
        ? 'Please allow access to your microphone to continue.\nPlease click the lock icon on the left of your address bar, allow microphone and then refresh the page.'
        : `Please allow access to your microphone to continue.\nPlease refresh and click 'allow' when asked to give Mable permission to access your microphone. If this doesn't work and you are unsure how to change the access settings in ${browserType}:`,
    helpLink: deviceAccessHelpLink,
    helpText: 'Click here for help'
  } as ErrorMessage
})

onMounted(() => {
  activeScreenerStore.sendSectionChange({ section: 'TechTestView' })
})

/**
 * Watch the devicesList and set the selectedDevice to the first device in the list
 * Device list may change if at the first step or device kind changes
 */
watch(
  () => devicesList.value,
  (newDevicesList) => {
    if (currentDeviceType.value === 'audiooutput') {
      stopTracks()
    }
    soundPicked.value = false // clear soundPicked flag
    if (!newDevicesList.length) {
      if (currentDeviceType.value === 'audioinput') {
        lastSelectedInputDeviceId.value = ''
      } else if (currentDeviceType.value === 'audiooutput') {
        lastSelectedOutputDeviceId.value = ''
      }
      setSelectedDeviceProgrammatically('')
      return
    }

    const desiredDeviceId =
      currentDeviceType.value === 'audioinput'
        ? lastSelectedInputDeviceId.value || selectedAudioInputDeviceId.value
        : currentDeviceType.value === 'audiooutput'
          ? lastSelectedOutputDeviceId.value || selectedAudioOutputDeviceId.value
          : ''

    const preferredDevice = desiredDeviceId
      ? newDevicesList.find((device) => device.deviceId === desiredDeviceId)
      : null

    const deviceToSelect = preferredDevice || newDevicesList[0]

    if (!deviceToSelect) return

    const nextDeviceId = (deviceToSelect.deviceId ?? '') as string
    logger.info('Updating selected device for current step', {
      deviceId: nextDeviceId,
      deviceKind: currentDeviceType.value
    })
    setSelectedDeviceProgrammatically(nextDeviceId)

    if (currentDeviceType.value === 'audioinput') {
      onInputDeviceChange(nextDeviceId)
      lastSelectedInputDeviceId.value = nextDeviceId
    } else if (currentDeviceType.value === 'audiooutput') {
      lastSelectedOutputDeviceId.value = nextDeviceId
    }
  },
  { immediate: true }
)

/**
 * Watch the selectedDevice and take appropriate action depending on the device kind
 */
watch(
  () => selectedDevice.value,
  (device) => {
    logger.info('Selected device changed: 1', device)
    if (!device || suppressSelectedDeviceWatch) return

    if (currentDeviceType.value === 'audioinput') {
      soundPicked.value = false
      lastSelectedInputDeviceId.value = device
      onInputDeviceChange(device)
    }

    if (currentDeviceType.value === 'audiooutput') {
      shouldResumeOutputAfterBlur.value = false
      stopTracks()
      soundPicked.value = false
      lastSelectedOutputDeviceId.value = device || ''
      selectedAudioOutputDeviceId.value = device || ''
      updateOutputDevice(device).catch((error: unknown) => {
        logger.error('Failed to change output device', error)
      })
    }

    if (deviceSelect.value) {
      deviceSelect.value.blur()
    }
  }
)

watch(
  () => selectedAudioInputDeviceId.value,
  (newDeviceId) => {
    lastSelectedInputDeviceId.value = newDeviceId || ''
    if (currentDeviceType.value !== 'audioinput') return
    if (selectedDevice.value === (newDeviceId || '')) return

    logger.info('Syncing selected device with store audio input selection', newDeviceId)
    setSelectedDeviceProgrammatically((newDeviceId || '') as string)
  }
)

watch(
  () => selectedAudioOutputDeviceId.value,
  (newDeviceId) => {
    lastSelectedOutputDeviceId.value = newDeviceId || ''
    if (currentDeviceType.value !== 'audiooutput') return
    if (!newDeviceId) return

    const currentIsFallback = selectedDevice.value === '' || selectedDevice.value === newDeviceId

    if (currentIsFallback) {
      logger.info('Keeping audio output selection unchanged', {
        current: selectedDevice.value,
        store: newDeviceId
      })
      return
    }

    logger.info('Syncing selected device with store audio output selection', newDeviceId)
    setSelectedDeviceProgrammatically((newDeviceId || '') as string)
  }
)

/**
 * Watch the currentStep and reset the soundPicked flag
 */
async function onInputDeviceChange(deviceId: string) {
  logger.info('Setting up new input device', deviceId)

  playbackPlayed.value = false

  try {
    await changeInputDevice(deviceId)
    waitForSound()
  } catch (error) {
    logger.error('Failed to change input device', error)
  }
}

/**
 * Wait for sound from the selected microphone
 */
function waitForSound() {
  clearManagedTimeout(noAudioInputTimeoutId)
  noAudioInputTimeoutId.value = setManagedTimeout(() => {
    showNoAudioInputError.value = true
    if (audioAnalyser.value && typeof audioAnalyser.value.triggerAutoRefresh === 'function') {
      audioAnalyser.value.triggerAutoRefresh()
    }
  }, NO_INPUT_TIMEOUT)
}

/**
 * Sound picked callback
 */
function onSoundPicked() {
  if (soundPicked.value === true) return

  soundPicked.value = true
  clearManagedTimeout(noAudioInputTimeoutId)
  if (showNoAudioInputError.value) {
    showNoAudioInputError.value = false
  }
}

/**
 * Playback finished callback
 */
function onPlaybackFinished() {
  if (playbackPlayed.value === true) return
  playbackPlayed.value = true
}

/**
 * Store the selected device id and move to the next step
 */
const onConfirm = async () => {
  storeAudioDeviceId(currentDeviceType.value, selectedDevice.value)

  if (currentStep.value === Object.keys(steps).length - 1) {
    await logTechTestSummary()
    emit('tech-test-finished')
    return
  }

  currentStep.value++

  waitForSound()
}

const onCloseDevicePrompt = () => {
  mediaDevicesStore.setRequiredAudioDevicePrompt(false)
}
/**
 * Select the next device in the list
 */
const selectNextDevice = () => {
  const currentIndex = devicesList.value.findIndex(
    (device) => device.deviceId === selectedDevice.value
  )
  const nextIndex = currentIndex + 1
  if (nextIndex >= devicesList.value.length) {
    selectedDevice.value = devicesList.value[0].deviceId as string
  } else {
    selectedDevice.value = devicesList.value[nextIndex].deviceId as string
  }
}

/**
 * Play the music
 */
const onPlayClick = () => {
  audioAnalyser.value?.onPlayMusicClick().catch((error: unknown) => {
    logger.error('Failed to play music', error)
  })
}

/**
 * Go back to the previous step
 */
const onBackClick = () => {
  if (currentDeviceType.value === 'audiooutput') {
    stopTracks()
  }
  currentStep.value--
}
</script>
<style lang="scss" scoped>
.audioSettingsModalBackdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: var(--full-vw);
  height: var(--full-vh);
  background-color: rgba(0, 0, 0, 0.3);
  z-index: 100;
}

.audioSettingsModal {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: $white;
  width: calc(var(--full-vw) * 0.9);
  max-width: 600px;
  border-radius: 10px;

  padding: 2rem;
  display: flex;
  flex-direction: column;

  &.noPermissions {
    padding: 0rem;

    .body {
      padding: 0rem;
    }
  }

  .micPermissionsError {
    display: flex;
    align-items: center;
    gap: 1rem;
    max-width: calc(var(--full-vw) * 0.65);
    background-color: $danger;
    font-size: 1.25rem;
    padding: 1rem;
    border-radius: 5px;
    color: $white;

    .note,
    .helpLink {
      font-size: 0.9rem;
    }

    a {
      color: $dark;
    }
  }

  .header {
    width: 100%;
    display: flex;
    align-items: center;
    padding-bottom: 0.5rem;
    border-bottom: 1px solid $secondary;
    justify-content: space-between;
  }

  .body {
    display: flex;
    flex-direction: column;
    padding: 2rem 0;

    .deviceSelection {
      display: flex;
      flex-direction: column;
      gap: 0.75rem;

      .info {
        display: flex;
        gap: 0.5rem;

        position: relative;
        padding: 0.75rem 1.25rem;
        border: 1px solid transparent;
        border-radius: 0.35rem;
        font-size: 0.8rem;
        // color: #0c5460;
        color: darken($info, 65%);
        background-color: $info;
        border-color: darken($info, 10%);

        &.error {
          color: $white;
          background-color: $danger;
        }

        .callToAction {
          color: $primary;
          cursor: pointer;
          font-weight: 600;
          text-decoration: underline;
        }
      }
    }
  }

  .footer {
    display: flex;
    justify-content: space-between;
  }
}
</style>
